import { useState, useEffect, useCallback } from 'preact/hooks'
import type { CloudNote, ChatSession, ChatAttachment } from '../types'
import { NOTE_TYPE_LABELS } from '../types'
import {
  listCloudNotes,
  listCloudSessions,
  loadCloudSession,
  downloadBlob,
  downloadBlobAsText,
} from '../services/api'

/**
 * "+" attachment dialog mirroring the mobile app's
 * `_showAttachmentOptionsMenu` flow in `text_chat_screen.dart`. Lets the user
 * pick a PDF / image / text file from disk, attach a cloud note / document /
 * image, or reference an existing chat session. Each option produces zero
 * or more `ChatAttachment`s that the chat input bubbles up to send.
 *
 * Cloud-mode protocol note: the backend's `format_message_for_openai`
 * inlines image attachments as `image_url` parts, decodes `text/plain`/
 * `text/html` attachments into prefixed text blocks, and forwards
 * `application/pdf` as a `document` block. Chat references piggyback on
 * the text/plain branch — they become part of the user message context
 * the same way the mobile app's `text/chat-reference` preprocessor does.
 */
type Mode =
  | 'menu'
  | 'cloud-notes'    // text/mixed notes
  | 'cloud-docs'     // AI-generated documents / PDFs
  | 'cloud-images'   // AI-generated or attached images
  | 'chat-sessions'  // reference an existing conversation

interface AttachmentPickerProps {
  onClose: () => void
  onAttach: (attachments: ChatAttachment[]) => void
  /** Optional: hide the chat-reference option (e.g. on landing). */
  allowChatReference?: boolean
  /** Persona id used to pre-filter chat sessions for the chat-reference branch. */
  personaId?: string
}

/** Convert a Uint8Array to base64 without choking on large allocations.
 *  btoa(String.fromCharCode(...arr)) blows up the call stack on multi-MB
 *  binaries, so chunk in 32 KiB segments — same trick the encryption
 *  service uses for ciphertexts. */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

/** Read a File from <input type=file> as base64. */
async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer()
  return bytesToBase64(new Uint8Array(buf))
}

/** Find the first cloud blob key in a note's attachments. */
function noteBlobKey(note: CloudNote): string | null {
  if (!Array.isArray(note.attachments)) return null
  for (const att of note.attachments) {
    const k = (att.blobKey as string | undefined) || (att.blob_key as string | undefined)
    if (k) return k
    const path = att.file_path || att.filePath
    if (path) {
      const parts = (path as string).split('/')
      if (parts.length >= 4 && /^[0-9a-f]{8,}$/.test(parts[0])) return path as string
    }
  }
  return null
}

export function AttachmentPicker({ onClose, onAttach, allowChatReference = true, personaId }: AttachmentPickerProps) {
  const [mode, setMode] = useState<Mode>('menu')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Esc closes the modal — matches the FullscreenImageViewer ergonomics.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const pickFromInput = useCallback((accept: string, multiple: boolean) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = accept
    input.multiple = multiple
    input.onchange = async () => {
      const files = Array.from(input.files || [])
      if (files.length === 0) return
      setBusy(true)
      setError(null)
      try {
        const attachments: ChatAttachment[] = []
        for (const file of files) {
          const data = await fileToBase64(file)
          const mimeType = file.type || (file.name.toLowerCase().endsWith('.md') ? 'text/markdown' : 'application/octet-stream')
          const att: ChatAttachment = {
            fileName: file.name,
            mimeType,
            data,
          }
          if (mimeType.startsWith('image/')) {
            att.previewUrl = URL.createObjectURL(file)
          }
          attachments.push(att)
        }
        onAttach(attachments)
        onClose()
      } catch (e) {
        setError((e as Error).message || 'Failed to read file')
      } finally {
        setBusy(false)
      }
    }
    input.click()
  }, [onAttach, onClose])

  const handlePickImageFile = useCallback(() => {
    pickFromInput('image/*', true)
  }, [pickFromInput])

  const handlePickDocFile = useCallback(() => {
    // Match what the backend can currently inline (PDF) and decode
    // (text/plain, text/markdown, text/html). Other types fall through as
    // application/octet-stream and the AI will only see the filename.
    pickFromInput('.pdf,.txt,.md,.markdown,.html,.htm,application/pdf,text/plain,text/markdown,text/html', true)
  }, [pickFromInput])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 96vw)',
          maxHeight: '90vh',
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 16px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-surface)',
        }}>
          {mode !== 'menu' && (
            <button
              class="btn btn-sm"
              onClick={() => { setMode('menu'); setError(null) }}
              style={{ marginRight: 4 }}
            >{'\u{2190}'}</button>
          )}
          <span style={{ flex: 1, fontWeight: 600 }}>
            {mode === 'menu' && 'Add attachment'}
            {mode === 'cloud-notes' && 'Attach a note'}
            {mode === 'cloud-docs' && 'Attach a document'}
            {mode === 'cloud-images' && 'Attach an image'}
            {mode === 'chat-sessions' && 'Reference a chat'}
          </span>
          <button class="btn btn-sm" onClick={onClose} title="Close (Esc)">{'\u{00D7}'}</button>
        </div>

        {error && (
          <div style={{ padding: '8px 16px', background: 'rgba(244,67,54,0.12)', color: '#f44336', fontSize: 12 }}>
            {error}
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto' }}>
          {mode === 'menu' && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <PickerOption
                icon={'\u{1F4F7}'}
                label="Image from your computer"
                description="JPG, PNG, GIF, WebP — sent as image part"
                onClick={handlePickImageFile}
                disabled={busy}
              />
              <PickerOption
                icon={'\u{1F4C4}'}
                label="PDF or text document"
                description="PDF, TXT, MD, HTML"
                onClick={handlePickDocFile}
                disabled={busy}
              />
              <Divider label="From your data" />
              <PickerOption
                icon={'\u{1F5BC}\u{FE0F}'}
                label="Image from Files"
                description="AI-generated images & saved images"
                onClick={() => setMode('cloud-images')}
              />
              <PickerOption
                icon={'\u{1F4D1}'}
                label="Document from Files"
                description="AI-generated documents & PDFs"
                onClick={() => setMode('cloud-docs')}
              />
              <PickerOption
                icon={'\u{1F4DD}'}
                label="Note"
                description="Text and mixed notes"
                onClick={() => setMode('cloud-notes')}
              />
              {allowChatReference && (
                <>
                  <Divider label="Other" />
                  <PickerOption
                    icon={'\u{1F4AC}'}
                    label="Reference an existing chat"
                    description="Attach the contents of a past conversation"
                    onClick={() => setMode('chat-sessions')}
                  />
                </>
              )}
            </div>
          )}

          {mode === 'cloud-notes' && (
            <CloudNotesList
              kind="note"
              onPick={(att) => { onAttach([att]); onClose() }}
              setError={setError}
            />
          )}
          {mode === 'cloud-docs' && (
            <CloudNotesList
              kind="document"
              onPick={(att) => { onAttach([att]); onClose() }}
              setError={setError}
            />
          )}
          {mode === 'cloud-images' && (
            <CloudNotesList
              kind="image"
              onPick={(att) => { onAttach([att]); onClose() }}
              setError={setError}
            />
          )}
          {mode === 'chat-sessions' && (
            <ChatSessionsList
              personaId={personaId}
              onPick={(att) => { onAttach([att]); onClose() }}
              setError={setError}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ── Menu helpers ──────────────────────────────────────────────────

function PickerOption({
  icon,
  label,
  description,
  onClick,
  disabled,
}: {
  icon: string
  label: string
  description?: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 16px',
        background: 'transparent',
        border: 'none',
        borderBottom: '1px solid var(--border)',
        color: 'var(--text)',
        cursor: disabled ? 'default' : 'pointer',
        textAlign: 'left',
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={(e) => { if (!disabled) (e.currentTarget as HTMLElement).style.background = 'rgba(45,165,160,0.08)' }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent' }}
    >
      <span style={{ fontSize: 22, width: 28, textAlign: 'center' }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>
        {description && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{description}</div>
        )}
      </div>
    </button>
  )
}

function Divider({ label }: { label: string }) {
  return (
    <div style={{
      padding: '10px 16px 4px',
      fontSize: 11,
      color: 'var(--text-muted)',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      background: 'var(--bg-surface)',
    }}>{label}</div>
  )
}

// ── Cloud notes list (used for notes / documents / images tabs) ───

function CloudNotesList({
  kind,
  onPick,
  setError,
}: {
  kind: 'note' | 'document' | 'image'
  onPick: (att: ChatAttachment) => void
  setError: (msg: string | null) => void
}) {
  const [notes, setNotes] = useState<CloudNote[]>([])
  const [loading, setLoading] = useState(true)
  const [picking, setPicking] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listCloudNotes()
      .then((all) => {
        if (cancelled) return
        let filtered: CloudNote[]
        if (kind === 'note') {
          // Plain / mixed notes that aren't AI-generated artefacts
          filtered = all.filter((n) => {
            const isArtefact = n.is_ai_generated_document || n.is_ai_generated_image || n.is_generated_pdf || n.is_app
            return !isArtefact && (n.type === 0 || n.type === 2 || n.type === 3)
          })
        } else if (kind === 'document') {
          filtered = all.filter((n) => (n.is_ai_generated_document && !n.is_app) || n.is_generated_pdf)
        } else {
          filtered = all.filter((n) => n.is_ai_generated_image || n.type === 2)
        }
        filtered.sort((a, b) => {
          const ta = a.last_updated_at || a.created_at || ''
          const tb = b.last_updated_at || b.created_at || ''
          return tb.localeCompare(ta)
        })
        setNotes(filtered)
        setLoading(false)
      })
      .catch((e) => {
        if (cancelled) return
        setError((e as Error).message || 'Failed to load')
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [kind, setError])

  const handlePick = useCallback(async (note: CloudNote) => {
    setError(null)
    setPicking(note.id)
    try {
      if (kind === 'image') {
        const key = noteBlobKey(note)
        if (!key) {
          setError('This image is not available in cloud storage.')
          setPicking(null)
          return
        }
        const bytes = await downloadBlob(key)
        if (!bytes) {
          setError('Failed to download image.')
          setPicking(null)
          return
        }
        // Detect MIME type from magic bytes (matches downloadBlobAsUrl)
        let mimeType = 'image/png'
        if (bytes[0] === 0xFF && bytes[1] === 0xD8) mimeType = 'image/jpeg'
        else if (bytes[0] === 0x47 && bytes[1] === 0x49) mimeType = 'image/gif'
        else if (bytes[0] === 0x52 && bytes[1] === 0x49) mimeType = 'image/webp'
        onPick({
          fileName: note.title || 'image',
          mimeType,
          data: bytesToBase64(bytes),
        })
      } else if (kind === 'document') {
        // Documents are stored as HTML or PDF in the blob. PDFs go through
        // as application/pdf so the backend forwards a document block; HTML/
        // markdown documents are sent as text/plain so the backend's text
        // branch decodes and inlines them in the user message context.
        const key = noteBlobKey(note)
        if (note.is_generated_pdf && key) {
          const bytes = await downloadBlob(key)
          if (!bytes) {
            setError('Failed to download PDF.')
            setPicking(null)
            return
          }
          onPick({
            fileName: `${note.title || 'document'}.pdf`,
            mimeType: 'application/pdf',
            data: bytesToBase64(bytes),
          })
        } else {
          let text: string | null = null
          if (key) text = await downloadBlobAsText(key)
          if (!text) text = note.content || null
          if (!text) {
            setError('Document has no readable content.')
            setPicking(null)
            return
          }
          const data = btoa(unescape(encodeURIComponent(text)))
          onPick({
            fileName: `${note.title || 'document'}.html`,
            mimeType: 'text/html',
            data,
          })
        }
      } else {
        // kind === 'note' — send the title + body as a text/plain attachment.
        // Mirrors how the mobile app's text-only notes are inlined.
        const body = note.content || ''
        const text = `# ${note.title || 'Untitled note'}\n\n${body}${note.transcription ? `\n\n--- Transcription ---\n${note.transcription}` : ''}`
        const data = btoa(unescape(encodeURIComponent(text)))
        onPick({
          fileName: `${note.title || 'note'}.txt`,
          mimeType: 'text/plain',
          data,
        })
      }
    } catch (e) {
      setError((e as Error).message || 'Failed to attach')
    } finally {
      setPicking(null)
    }
  }, [kind, onPick, setError])

  if (loading) return <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
  if (notes.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
        Nothing to show here yet.
      </div>
    )
  }

  return (
    <div>
      {notes.map((note) => (
        <button
          key={note.id}
          onClick={() => handlePick(note)}
          disabled={picking !== null}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 16px',
            width: '100%',
            background: picking === note.id ? 'rgba(45,165,160,0.12)' : 'transparent',
            border: 'none',
            borderBottom: '1px solid var(--border)',
            color: 'var(--text)',
            cursor: picking ? 'default' : 'pointer',
            textAlign: 'left',
          }}
        >
          <span style={{ fontSize: 18, width: 24, textAlign: 'center' }}>
            {kind === 'image' ? '\u{1F5BC}\u{FE0F}' : kind === 'document' ? (note.is_generated_pdf ? '\u{1F4C4}' : '\u{1F4D1}') : '\u{1F4DD}'}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {note.title || 'Untitled'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              {NOTE_TYPE_LABELS[note.type ?? 0]}
              {note.last_updated_at && ` · ${new Date(note.last_updated_at).toLocaleDateString()}`}
            </div>
          </div>
          {picking === note.id && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>…</span>}
        </button>
      ))}
    </div>
  )
}

// ── Chat sessions list ─────────────────────────────────────────────

function ChatSessionsList({
  personaId,
  onPick,
  setError,
}: {
  personaId?: string
  onPick: (att: ChatAttachment) => void
  setError: (msg: string | null) => void
}) {
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [loading, setLoading] = useState(true)
  const [picking, setPicking] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    // Match the persona-scoped flow: pre-filter by the active persona so the
    // user picks among "this persona's" past chats. Falls back to the full
    // list if no persona is given.
    listCloudSessions(personaId, 50, 0)
      .then((res) => {
        if (cancelled) return
        const filtered = res.sessions.filter((s) => (s.message_count ?? 0) > 0)
        setSessions(filtered)
        setLoading(false)
      })
      .catch((e) => {
        if (cancelled) return
        setError((e as Error).message || 'Failed to load sessions')
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [personaId, setError])

  const handlePick = useCallback(async (session: ChatSession) => {
    setError(null)
    setPicking(session.id)
    try {
      const loaded = await loadCloudSession(session.id)
      const messages = loaded.messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
      if (messages.length === 0) {
        setError('Session has no messages to reference.')
        setPicking(null)
        return
      }
      const buffer: string[] = []
      buffer.push(`=== Referenced Conversation: ${session.summary || 'Untitled chat'} ===`)
      buffer.push(`Started: ${session.started_at}`)
      buffer.push(`Messages: ${messages.length}`)
      buffer.push('')
      for (const m of messages) {
        const role = m.role === 'user' ? 'User' : (session.persona || 'Assistant')
        buffer.push(`[${role}]:`)
        // Strip backend timestamp envelope so referenced text reads cleanly.
        const inner = (m.content || '').replace(/^\[user time:[^\]]*\]\s*/, '').replace(/^VC_START:/, '')
        buffer.push(inner)
        buffer.push('')
      }
      const text = buffer.join('\n')
      const data = btoa(unescape(encodeURIComponent(text)))
      onPick({
        fileName: `Chat: ${session.summary || 'Untitled'}.txt`,
        mimeType: 'text/plain',
        data,
      })
    } catch (e) {
      setError((e as Error).message || 'Failed to load chat')
    } finally {
      setPicking(null)
    }
  }, [onPick, setError])

  if (loading) return <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
  if (sessions.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
        No previous conversations to reference.
      </div>
    )
  }
  return (
    <div>
      {sessions.map((session) => (
        <button
          key={session.id}
          onClick={() => handlePick(session)}
          disabled={picking !== null}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 16px',
            width: '100%',
            background: picking === session.id ? 'rgba(45,165,160,0.12)' : 'transparent',
            border: 'none',
            borderBottom: '1px solid var(--border)',
            color: 'var(--text)',
            cursor: picking ? 'default' : 'pointer',
            textAlign: 'left',
          }}
        >
          <span style={{ fontSize: 18, width: 24, textAlign: 'center' }}>{'\u{1F4AC}'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {session.summary || 'Untitled chat'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              {(session.message_count ?? 0)} messages · {new Date(session.started_at).toLocaleDateString()}
            </div>
          </div>
          {picking === session.id && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>…</span>}
        </button>
      ))}
    </div>
  )
}
